(() => {
  const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
  const TOLERANCE = 1;
  const DRAG_START_THRESHOLD = 4;
  const MAX_OVERSCROLL = 88;

  const defineElement = (name, definition) => {
    if (!customElements.get(name)) {
      customElements.define(name, definition);
    }
  };

  class CarouselLayout extends HTMLElement {}

  class CarouselLayoutWrapper extends HTMLElement {
    constructor() {
      super();
      this.handleResize = this.updateButtonState.bind(this);
      this.handleScroll = this.updateButtonState.bind(this);
      this.handlePointerDown = this.onPointerDown.bind(this);
      this.handlePointerMove = this.onPointerMove.bind(this);
      this.handlePointerUp = this.onPointerUp.bind(this);
      this.handleClick = this.onClick.bind(this);
      this.handleDragStart = this.onDragStart.bind(this);
      this.handleReboundEnd = this.onReboundEnd.bind(this);
      this.previousClickHandler = this.createStepHandler(-1);
      this.nextClickHandler = this.createStepHandler(1);
    }

    connectedCallback() {
      if (this.isConnectedToCarouselLayout === true) return;

      this.viewport = this.querySelector('[data-carousel-layout-viewport]') ?? this.querySelector('carousel-layout');
      if (!(this.viewport instanceof HTMLElement)) return;

      this.previousButton = this.querySelector('[data-carousel-layout-prev]');
      this.nextButton = this.querySelector('[data-carousel-layout-next]');

      this.setButtonListener(this.previousButton, this.previousClickHandler, true);
      this.setButtonListener(this.nextButton, this.nextClickHandler, true);
      this.toggleViewportListeners(true);
      window.addEventListener('resize', this.handleResize, { passive: true });

      this.isConnectedToCarouselLayout = true;
      this.updateButtonState();
    }

    disconnectedCallback() {
      if (this.isConnectedToCarouselLayout !== true) return;

      this.setButtonListener(this.previousButton, this.previousClickHandler, false);
      this.setButtonListener(this.nextButton, this.nextClickHandler, false);
      this.toggleViewportListeners(false);
      window.removeEventListener('resize', this.handleResize, { passive: true });
      this.isConnectedToCarouselLayout = false;
    }

    getItems() {
      const track = this.getTrack();
      if (!(track instanceof HTMLElement)) return [];

      return Array.from(track.children).filter((item) => item instanceof HTMLElement);
    }

    getTrack() {
      if (!(this.viewport instanceof HTMLElement)) return null;

      return this.viewport.querySelector('[data-carousel-layout-track]') ?? this.viewport;
    }

    getVisibleWidth() {
      return this.viewport?.clientWidth ?? 0;
    }

    getTrackGap() {
      const track = this.getTrack();
      if (!(track instanceof HTMLElement)) return 0;

      const styles = window.getComputedStyle(track);
      const gapValue = styles.columnGap || styles.gap;
      const gap = Number.parseFloat(gapValue);

      return Number.isFinite(gap) ? gap : 0;
    }

    getMaxScrollLeft() {
      if (!(this.viewport instanceof HTMLElement)) return 0;

      return Math.max(0, this.viewport.scrollWidth - this.getVisibleWidth());
    }

    getAdjacentStepPosition(position, direction) {
      const visibleWidth = this.getVisibleWidth();
      const maxScrollLeft = this.getMaxScrollLeft();

      if (visibleWidth <= 1 || maxScrollLeft <= 1) {
        return 0;
      }

      const stepDistance = visibleWidth + this.getTrackGap();

      return Math.min(maxScrollLeft, Math.max(0, position + stepDistance * direction));
    }

    scrollToAnchor(target, behavior = 'smooth') {
      if (!(this.viewport instanceof HTMLElement)) return;

      this.viewport.scrollTo({
        left: target,
        behavior,
      });
    }

    scrollByStep(direction) {
      const currentPosition = this.viewport?.scrollLeft ?? 0;
      const target = this.getAdjacentStepPosition(currentPosition, direction);
      this.scrollToAnchor(target, 'smooth');
    }

    isDragScrollEnabled() {
      if (!(this.viewport instanceof HTMLElement)) return false;
      return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
    }

    resetDragTracking() {
      this.dragPointerId = null;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragStartScrollLeft = 0;
      this.dragDeltaX = 0;
      this.dragOverscroll = 0;
      this.isDragging = false;
    }

    getResistedOverscroll(distance) {
      if (distance === 0) return 0;

      const magnitude = Math.abs(distance);
      const resistedMagnitude = MAX_OVERSCROLL * (1 - 1 / (magnitude / 140 + 1));
      return Math.sign(distance) * resistedMagnitude;
    }

    setOverscroll(offset) {
      if (!(this.viewport instanceof HTMLElement)) return;

      this.dragOverscroll = offset;
      this.viewport.style.setProperty('--carousel-layout-overscroll-offset', `${offset}px`);
    }

    clearOverscroll(instant = false) {
      if (!(this.viewport instanceof HTMLElement)) return;

      if (instant) {
        this.viewport.setAttribute('data-rebound', 'false');
      }

      this.setOverscroll(0);
    }

    startRebound(overscrollOffset) {
      if (!(this.viewport instanceof HTMLElement)) return;
      if (Math.abs(overscrollOffset) <= TOLERANCE) return;

      this.viewport.setAttribute('data-rebound', 'true');
      this.setOverscroll(0);
    }

    onPointerDown(event) {
      if (!(this.viewport instanceof HTMLElement)) return;
      if (event.button !== 0) return;
      if (this.isDragScrollEnabled() === false) return;

      const maxScrollLeft = this.getMaxScrollLeft();
      if (maxScrollLeft <= 1) return;

      this.dragPointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartScrollLeft = this.viewport.scrollLeft;
      this.dragDeltaX = 0;
      this.dragOverscroll = 0;
      this.didDrag = false;
      this.isDragging = false;
      this.viewport.setAttribute('data-rebound', 'false');
    }

    onPointerMove(event) {
      if (!(this.viewport instanceof HTMLElement)) return;
      if (this.dragPointerId !== event.pointerId) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      this.dragDeltaX = deltaX;

      if (this.isDragging !== true) {
        if (Math.abs(deltaX) <= DRAG_START_THRESHOLD && Math.abs(deltaY) <= DRAG_START_THRESHOLD) return;

        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.resetDragTracking();
          return;
        }

        this.isDragging = true;
        this.didDrag = true;
        this.viewport.setAttribute('data-dragging', 'true');

        if (this.viewport.hasPointerCapture(event.pointerId) === false) {
          this.viewport.setPointerCapture(event.pointerId);
        }
      }

      if (this.isDragging !== true) return;

      const maxScrollLeft = this.getMaxScrollLeft();
      const nextScrollLeft = this.dragStartScrollLeft - deltaX;
      const boundedScrollLeft = Math.min(maxScrollLeft, Math.max(0, nextScrollLeft));
      let overscrollOffset = 0;

      if (nextScrollLeft < 0) {
        overscrollOffset = this.getResistedOverscroll(-nextScrollLeft);
      } else if (nextScrollLeft > maxScrollLeft) {
        overscrollOffset = this.getResistedOverscroll(maxScrollLeft - nextScrollLeft);
      }

      this.viewport.scrollLeft = boundedScrollLeft;
      this.setOverscroll(overscrollOffset);

      if (event.cancelable) {
        event.preventDefault();
      }
    }

    onPointerUp(event) {
      if (!(this.viewport instanceof HTMLElement)) return;
      if (this.dragPointerId !== event.pointerId) return;

      if (this.viewport.hasPointerCapture(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }

      const overscrollOffset = this.dragOverscroll ?? 0;
      const hadOverscroll = Math.abs(overscrollOffset) > TOLERANCE;
      this.resetDragTracking();
      this.viewport.removeAttribute('data-dragging');

      if (hadOverscroll) {
        this.dragOverscroll = overscrollOffset;
        this.startRebound(overscrollOffset);
      } else {
        this.clearOverscroll(true);
      }
    }

    onClick(event) {
      if (this.didDrag !== true) return;

      event.preventDefault();
      event.stopPropagation();
      this.didDrag = false;
    }

    onDragStart(event) {
      if (this.isDragScrollEnabled() === true) {
        event.preventDefault();
      }
    }

    onReboundEnd(event) {
      if (event.propertyName !== 'transform') return;
      if (!(this.viewport instanceof HTMLElement)) return;

      if (Math.abs(this.dragOverscroll ?? 0) <= TOLERANCE) {
        this.viewport.setAttribute('data-rebound', 'false');
      }
    }

    updateButtonState() {
      if (!(this.viewport instanceof HTMLElement)) return;

      const maxScrollLeft = this.getMaxScrollLeft();
      const canScroll = maxScrollLeft > 1;
      const isAtStart = this.viewport.scrollLeft <= TOLERANCE;
      const isAtEnd = this.viewport.scrollLeft >= maxScrollLeft - TOLERANCE;

      this.setAttribute('data-can-scroll', canScroll ? 'true' : 'false');
      this.updateButton(this.previousButton, canScroll == false || isAtStart);
      this.updateButton(this.nextButton, canScroll == false || isAtEnd);
    }

    createStepHandler(direction) {
      return (event) => {
        event.preventDefault();
        this.clearOverscroll(true);
        this.scrollByStep(direction);
      };
    }

    toggleViewportListeners(add) {
      if (!(this.viewport instanceof HTMLElement)) return;

      const method = add ? 'addEventListener' : 'removeEventListener';
      this.viewport[method]('scroll', this.handleScroll, { passive: true });
      this.viewport[method]('pointerdown', this.handlePointerDown);
      this.viewport[method]('pointermove', this.handlePointerMove);
      this.viewport[method]('pointerup', this.handlePointerUp);
      this.viewport[method]('pointercancel', this.handlePointerUp);
      this.viewport[method]('lostpointercapture', this.handlePointerUp);
      this.viewport[method]('click', this.handleClick, true);
      this.viewport[method]('dragstart', this.handleDragStart);
      this.viewport[method]('transitionend', this.handleReboundEnd);
    }

    setButtonListener(button, handler, add) {
      if (!(button instanceof HTMLButtonElement)) return;

      const method = add ? 'addEventListener' : 'removeEventListener';
      button[method]('click', handler);
    }

    updateButton(button, disabled) {
      if (!(button instanceof HTMLButtonElement)) return;

      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

  }

  defineElement('carousel-layout', CarouselLayout);
  defineElement('carousel-layout-wrapper', CarouselLayoutWrapper);
})();
